import React, { useState, useRef, useCallback } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';

const dropzoneStyles = {
    border: '2px dashed #bdbdbd',
    borderRadius: 8,
    padding: '24px 16px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    backgroundColor: '#fafafa',
    '&:hover': {
        borderColor: '#1976d2',
        backgroundColor: '#f0f7ff',
    },
};

const dropzoneActiveStyles = {
    borderColor: '#1976d2',
    backgroundColor: '#e3f2fd',
};

const previewContainerStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    mt: 2,
};

const previewItemStyles = {
    position: 'relative',
    width: 80,
    height: 80,
    borderRadius: 1,
    overflow: 'hidden',
    border: '1px solid #e0e0e0',
};

export const ImageDropzone = ({ images = [], onChange }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const inputRef = useRef(null);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files).filter(file =>
            file.type.startsWith('image/')
        );
        if (files.length > 0) {
            onChange([...images, ...files]);
        }
    }, [images, onChange]);

    const handleClick = () => {
        inputRef.current?.click();
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            onChange([...images, ...files]);
        }
        e.target.value = '';
    };

    const previewUrls = React.useMemo(() => {
        return images.map((file) => URL.createObjectURL(file));
    }, [images]);

    React.useEffect(() => {
        return () => {
            previewUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [previewUrls]);

    const handleRemove = (index) => {
        const updated = images.filter((_, i) => i !== index);
        onChange(updated);
    };

    return (
        <Box>
            <Box
                sx={{
                    ...dropzoneStyles,
                    ...(isDragOver ? dropzoneActiveStyles : {}),
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                />
                <CloudUploadIcon sx={{ fontSize: 40, color: isDragOver ? '#1976d2' : '#9e9e9e' }} />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Arrastra imágenes aquí o haz clic para seleccionar
                </Typography>
            </Box>

            {images.length > 0 && (
                <Box sx={previewContainerStyles}>
                    {images.map((file, index) => (
                        <Box key={`${file.name}-${index}`} sx={previewItemStyles}>
                            <img
                                src={previewUrls[index]}
                                alt={file.name}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemove(index);
                                }}
                                sx={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                    backgroundColor: 'rgba(255,255,255,0.8)',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                                    padding: '2px',
                                }}
                            >
                                <DeleteIcon sx={{ fontSize: 16, color: '#e53935' }} />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};
